<template>
  <div class="purchase-pass-quantity-selector">
    <v-card>
      <v-expansion-panels v-model="expandedPanels">
        <span v-if="!hasAnyPass">
          <p class="text-center text-caption">No passes available.</p>
        </span>
        <v-expansion-panel v-if="hasAnyPass" value="passes">
          <v-expansion-panel-title>
            <v-icon class="mr-4">fal fa-ticket</v-icon>
            <h1>PASSES</h1>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="pa-0">
            <v-table v-if="display.mdAndUp.value" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Type</th>
                    <th class="text-center">Quantity</th>
                    <th></th>
                    <th class="text-right">Price</th>
                    <th class="text-right">Service Fee</th>
                    <th class="text-right">Conv. Fee</th>
                    <th class="text-right">Tax</th>
                    <th class="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="section in sections" :key="section.key">
                    <tr class="pass-section-header">
                      <td colspan="8">
                        <span class="font-weight-bold">{{
                          section.title
                        }}</span>
                        <span class="text-grey">
                          &middot; {{ section.subtitle }}
                        </span>
                      </td>
                    </tr>
                    <purchase-pass-quantity-selector-row
                      v-for="passType in section.passes"
                      :key="passType.id!"
                      :passType="passType"
                      :purchase="purchase"
                      :setQuantityCaller="setQuantityCaller"
                    />
                  </template>
                  <tr>
                    <td>
                      <v-fade-transition>
                        <div
                          class="text-h6 text-info py-3"
                          v-if="purchase?.payments?.length"
                        >
                          <span class="pr-2">Balance:</span>
                          <span
                            :class="{
                              'error--text': (purchase?.balance ?? -1) < 0,
                            }"
                          >
                            {{ formatCurrency(purchase.balance) }}
                          </span>
                        </div>
                      </v-fade-transition>
                    </td>
                    <td :colspan="display.mdAndUp.value ? 3 : 2">
                      <div class="text-h6 text-right py-3">
                        <span class="pr-2">Subtotal:</span>
                        {{ formatCurrency(totalPrice) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
            <div v-else>
              <div v-for="section in sections" :key="section.key">
                <div class="pass-section-header-mobile pa-2">
                  <span class="font-weight-bold">{{ section.title }}</span>
                  <span class="text-grey">&middot; {{ section.subtitle }}</span>
                </div>
                <purchase-pass-quantity-selector-row
                  v-for="passType in section.passes"
                  :key="passType.id!"
                  :passType="passType"
                  :purchase="purchase"
                  :setQuantityCaller="setQuantityCaller"
                />
              </div>
              <v-divider class="mt-2"></v-divider>
              <v-row class="border-0">
                <v-col class="text-right">
                  <div class="total-container text-h6 pa-2">
                    <span class="pr-2 font-weight-bold">Subtotal:</span>
                    {{ formatCurrency(totalPrice) }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import { formatCurrency, setupPurchaseReloadHandling } from "@common/utils";
import {
  TicketPurchaseDtoViewModel,
  PublicPurchaseServiceViewModel,
} from "@/viewmodels.g";
import { partitionPassTypesByCategory } from "@common/purchase/pass-quantity-selector-logic";
import PurchasePassQuantitySelectorRow from "./purchase-pass-quantity-selector-row.vue";

const display = useDisplay();
const expandedPanels = ref(["passes"]);

const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  // The series quantity-update caller, supplied by the consuming app so the selector works
  // against either the public or the private (agent) purchase service. The member type is
  // structurally identical across both ViewModels.
  setQuantityCaller: PublicPurchaseServiceViewModel["setSeriesPassTypeQuantity"];
}>();

// Configure the shared caller once here rather than per row: a quantity update then triggers a
// single purchase reload (not one per rendered row), and edits to the one purchase are debounced.
props.setQuantityCaller.setConcurrency("debounce");
setupPurchaseReloadHandling(props.purchase, props.setQuantityCaller);

// Both the desktop table and the mobile cards iterate this one source, so a section's markup,
// labels, and row props (like setQuantityCaller) live in one place and can't drift between the two
// layouts. Empty sections are dropped so a single-day-only series shows just one header, not a bare
// "Multi-Day Passes" heading.
const passGroups = computed(() =>
  partitionPassTypesByCategory(props.purchase.eventSeries?.passTypes ?? []),
);
const sections = computed(() =>
  [
    {
      key: "multi",
      title: "Multi-Day Passes",
      subtitle: "one ticket per included day",
      passes: passGroups.value.multiDay,
    },
    {
      key: "single",
      title: "Single-Day Passes",
      subtitle: "one ticket for one day",
      passes: passGroups.value.singleDay,
    },
  ].filter((s) => s.passes.length),
);
const hasAnyPass = computed(() => sections.value.length > 0);

const totalPrice = computed(() => props.purchase.grandTotal ?? 0);
</script>

<style scoped>
/* Set the section headers apart from the pass rows beneath them. */
.purchase-pass-quantity-selector :deep(.pass-section-header td),
.pass-section-header-mobile {
  background: rgb(var(--v-theme-surface-light, 238, 241, 245));
}
</style>
